import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Container, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { MainHeading, TextStyled } from '../../../ui/Elements'
import { FooterLogo, GooglePlay } from '../../../ui/AllImages'
import { APP_STORE_URL, PLAY_STORE_URL, redirectToAppStore } from '../../../utils/appStoreRedirect'
import { QR_DOWNLOAD_URL, getQrImageSrc } from '../../../utils/qrDownload'

const openStore = (url) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
}

const AppRedirect = () => {
    const { t } = useTranslation()

    useEffect(() => {
        redirectToAppStore()
    }, [])

    return (
        <RedirectWrapper className="container-fluid common-space">
            <Container>
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col lg={8} md={10} className="text-center">
                        <DownloadCard>
                            <AppIconWrapper>
                                <AppIcon>
                                    <img src={FooterLogo} alt="Sahab Al Alam" />
                                </AppIcon>
                            </AppIconWrapper>
                            <MainHeading className="mb-3">{t('AppDownloadTitle')}</MainHeading>
                            <TextStyled className="mb-5 text-muted max-w-600 mx-auto">
                                {t('AppDownloadSubtitle')}
                            </TextStyled>

                            <Row className="justify-content-center align-items-center g-4">
                                <Col md={6} className="d-flex flex-column align-items-center">
                                    <QRCardWrapper>
                                        <QRContainer>
                                            <QRScannerFrame>
                                                <QRCodeImageWrapper>
                                                    <img
                                                        src={getQrImageSrc(QR_DOWNLOAD_URL, 600)}
                                                        srcSet={`${getQrImageSrc(QR_DOWNLOAD_URL, 600)} 1x, ${getQrImageSrc(QR_DOWNLOAD_URL, 1000)} 2x`}
                                                        alt="Download QR Code"
                                                        loading="lazy"
                                                        width={200}
                                                        height={200}
                                                    />
                                                    <QRLogoOverlay>
                                                        <img src={FooterLogo} alt="" />
                                                    </QRLogoOverlay>
                                                </QRCodeImageWrapper>
                                            </QRScannerFrame>
                                        </QRContainer>
                                        <QRText className="text-white text-center mt-3 small">
                                            Scan with phone to download
                                        </QRText>
                                    </QRCardWrapper>
                                </Col>

                                <Col md={6} className="d-flex flex-column align-items-center gap-3">
                                    <StoreButton
                                        href={APP_STORE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={openStore(APP_STORE_URL)}
                                    >
                                        <span className="store-icon" aria-hidden="true">
                                            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.07 2.47.3 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42.14-1.38 1.83zM15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.21.67-2.93 1.49-.62.69-1.16 1.84-1.01 2.96 1.12.09 2.27-.56 2.95-1.39z"/>
                                            </svg>
                                        </span>
                                        <div className="btn-text">
                                            <span className="top-text">Download on the</span>
                                            <span className="bottom-text">App Store</span>
                                        </div>
                                    </StoreButton>

                                    <StoreButton
                                        href={PLAY_STORE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={openStore(PLAY_STORE_URL)}
                                    >
                                        <span className="store-icon" aria-hidden="true">
                                            <img src={GooglePlay} alt="" width="28" height="28" />
                                        </span>
                                        <div className="btn-text">
                                            <span className="top-text">GET IT ON</span>
                                            <span className="bottom-text">Google Play</span>
                                        </div>
                                    </StoreButton>
                                </Col>
                            </Row>
                        </DownloadCard>
                    </Col>
                </Row>
            </Container>
        </RedirectWrapper>
    )
}

const RedirectWrapper = styled.div`
    background: var(--bg);
    min-height: 100vh;
    display: flex;
    align-items: center;
`

const DownloadCard = styled.div`
    background: var(--white);
    border-radius: 24px;
    padding: 50px 40px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.03);
    
    .max-w-600 {
        max-width: 600px;
    }
`

const AppIconWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
`

const AppIcon = styled.div`
    width: 80px;
    height: 80px;
    background: var(--white);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    padding: 10px;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`

const QRCardWrapper = styled.div`
    background: linear-gradient(135deg, #002d40 0%, #001a26 100%);
    padding: 30px;
    border-radius: 24px;
    box-shadow: 0 12px 30px rgba(0, 45, 64, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.08);
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 18px 40px rgba(0, 45, 64, 0.3);
    }
`

const QRContainer = styled.div`
    background: var(--white);
    padding: 15px;
    border-radius: 18px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
`

const QRScannerFrame = styled.div`
    position: relative;
    padding: 10px;
    background: var(--white);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
`

const QRCodeImageWrapper = styled.div`
    position: relative;
    width: 180px;
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
        width: 100%;
        height: 100%;
        display: block;
    }

    .corner {
        position: absolute;
        width: 20px;
        height: 20px;
        border: 3px solid var(--themeColor);
        pointer-events: none;
    }

    .top-left {
        top: -10px;
        left: -10px;
        border-right: none;
        border-bottom: none;
        border-top-left-radius: 12px;
    }

    .top-right {
        top: -10px;
        right: -10px;
        border-left: none;
        border-bottom: none;
        border-top-right-radius: 12px;
    }

    .bottom-left {
        bottom: -10px;
        left: -10px;
        border-right: none;
        border-top: none;
        border-bottom-left-radius: 12px;
    }

    .bottom-right {
        bottom: -10px;
        right: -10px;
        border-left: none;
        border-top: none;
        border-bottom-right-radius: 12px;
    }
`

const QRLogoOverlay = styled.div`
    position: absolute;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    width: 28px;
    height: 28px;
    background: #fff;
    border-radius: 6px;
    padding: 2px;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`

const QRText = styled(TextStyled)`
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.9) !important;
`

const StoreButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    background: #111111;
    color: var(--white) !important;
    padding: 10px 18px;
    border-radius: 14px;
    text-decoration: none;
    width: fit-content;
    min-width: 0;
    max-width: 100%;
    transition: all 0.3s ease;
    border: 1px solid transparent;
    box-sizing: border-box;

    html[dir="rtl"] & {
        direction: rtl;
        padding: 10px 16px;
    }

    .store-icon {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        img, svg {
            width: 28px;
            height: 28px;
            display: block;
            object-fit: contain;
        }
    }

    .btn-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        flex: 0 1 auto;

        html[dir="rtl"] & {
            text-align: right;
            align-items: flex-end;
        }
    }

    .top-text {
        font-size: 10px;
        text-transform: none;
        opacity: 0.85;
        display: block;
        line-height: 1.2;
        white-space: nowrap;
    }

    .bottom-text {
        font-size: 16px;
        font-weight: 700;
        display: block;
        line-height: 1.25;
        white-space: nowrap;
    }

    &:hover {
        background: #222222;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }
`

export default AppRedirect
